import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { GlobalAlertService } from './global-alert.service';
import { UserService } from './user.service';
import { NgxDeeplinkerService } from 'ngx-deeplinker';
import { UserFormService } from './user-form.service';
import { UserFormRolesService } from './user-form-roles.service';
import { FormGroup } from '@angular/forms';
import { UserPatch } from '../model/user.model';
import { UserDialogStage, UserDialogComponent } from '../../dialogs/user-dialog/user-dialog.component';
import { BehaviorSubject } from 'rxjs';
import { UserStatusService } from './user-status.service';
import { SubscriptionTableService } from './subscription-table.service';
@Injectable({
  providedIn: 'root',
})
export class UserSubmitService {
  private readonly appPrefix = 'usr';

  private readonly MESSAGES = {
    REQUIRED: 'user.validation.required',
    CREATED_SUCCESSFULLY: 'user.notification.created_successfully',
    UPDATED_SUCCESSFULLY: 'user.notification.updated_successfully',
    BAD_REQUEST: 'user.notification.bad_request',
    NOT_CREATED: 'user.notification.not_created',
    NOT_UPDATED: 'user.notification.not_updated',
    ERROR_APPLYING_ROLES: 'user.notification.error_applying_roles',
    ERROR_APPLYING_SUBSCRIPTIONS: 'user.notification.error_applying_subscriptions',
    ECRM_ID_ALREADY_IN_USE: 'user.notification.ecrm_id_already_in_use',
    ECRM_CONTACT_ID_ALREADY_IN_USE: 'user.notification.ecrm_contact_id_already_in_use',
    SEND_ACTIVATION_EMAIL_ERROR: 'user.notification.send_activation_email_error',
    ACTIVATION_EMAIL_SENT: 'user.notification.activation_email_sent',
  };

  submittingUser$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private messageService: GlobalAlertService,
    private userService: UserService,
    private ngxDeeplinkerService: NgxDeeplinkerService,
    private userFormService: UserFormService,
    private userFormRolesService: UserFormRolesService,
    private dialog: MatDialog,
    private userStatusService: UserStatusService,
    private subscriptionTableService: SubscriptionTableService,
  ) {}

  async handleCreateSubmit(userForm: FormGroup): Promise<{ created: boolean; userId: string }> {
    if (!userForm.valid) {
      this.messageService.setError(this.translateService.instant(this.MESSAGES.REQUIRED));
      return { created: false, userId: '' };
    }

    this.submittingUser$.next(true);
    this.dialog.open(UserDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        initialStage: UserDialogStage.creating_user,
      },
    });

    try {
      const user = this.userFormService.prepareUserForSubmission(userForm);
      const response = await this.userService.createUser(user);
      this.ngxDeeplinkerService.returnHandler({ appPrefix: this.appPrefix, callbackValue: response.id });
      const userData = await this.userService.getUser(response.id);
      userForm.patchValue(userData);
      this.messageService.setSuccess(this.translateService.instant(this.MESSAGES.CREATED_SUCCESSFULLY));
      this.dialog.closeAll();
      this.userService.refetchUsers();

      return { created: true, userId: response.id };
    } catch (e) {
      console.error(e);
      this.dialog.closeAll();
      const errorMessage = e.error.message;
      if (errorMessage === 'ERR_BAD_REQUEST') {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.BAD_REQUEST));
      } else {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.NOT_CREATED));
      }

      return { created: false, userId: '' };
    } finally {
      this.submittingUser$.next(false);
    }
  }

  async handleEditSubmit(
    userForm: FormGroup,
    userId: string,
    canSubmitRoles: boolean,
    isOnboarding: boolean,
    redirect = false,
  ): Promise<{ updated: boolean }> {
    if (!userForm.valid) {
      this.messageService.setError(this.translateService.instant(this.MESSAGES.REQUIRED));
      return { updated: false };
    }

    this.submittingUser$.next(true);
    this.dialog.open(UserDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        initialStage: UserDialogStage.updating_user,
      },
    });

    const updatedUser = this.userFormService.prepareUserForSubmission(userForm);

    const updatedUserECRM: UserPatch = {
      federated_applications: [
        {
          application_name: 'ECRM_SSO',
          username: userForm.get('ecrm_id').value,
        },
      ],
    };

    try {
      await this.userService.updateUser(userId, updatedUser);

      if (userForm.get('ecrm_id').value) {
        await this.userService.updateUserECRM(userId, updatedUserECRM);
      }

      if (canSubmitRoles) {
        if (!isOnboarding) {
          try {
            await this.userFormRolesService.submitRoles(userId, userForm.getRawValue());
          } catch (error) {
            this.dialog.closeAll();
            console.error('Failed submitting roles', error);
            this.messageService.setError(this.translateService.instant(this.MESSAGES.ERROR_APPLYING_ROLES));
            return;
          }
        }

        try {
          await this.userFormRolesService.submitSubs(userId, userForm.getRawValue());
        } catch (error) {
          this.dialog.closeAll();
          console.error('Failed submitting subscriptions', error);
          this.messageService.setError(this.translateService.instant(this.MESSAGES.ERROR_APPLYING_SUBSCRIPTIONS));
          return;
        }
      }
      if (!redirect) {
        const userData = await this.userService.getUser(userId);
        userForm.patchValue(userData);
        userForm.setControl('phones', this.userFormService.createPhonesFormArray(userData.phones));
      }
      this.ngxDeeplinkerService.returnHandler({ appPrefix: this.appPrefix, callbackValue: userId });
      this.dialog.closeAll();
      this.messageService.setSuccess(this.translateService.instant(this.MESSAGES.UPDATED_SUCCESSFULLY));
      this.userService.refetchUsers();

      if (redirect) {
        setTimeout(() => {
          this.router.navigate([`details/${userId}/view`], {});
        }, 0);
      }
      return { updated: true };
    } catch (e) {
      console.error(e);
      this.dialog.closeAll();
      const errorMessage = e.error.message;

      if (errorMessage === 'ERR_BAD_REQUEST') {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.BAD_REQUEST));
      } else if (errorMessage === 'Unprocessable Entity' && e.error.detail.includes('UNIQUE_ALT_USERNAME')) {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.ECRM_ID_ALREADY_IN_USE));
      } else if (errorMessage === 'Bad Request' && e.error.detail.includes('ECRM_ID_USED')) {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.ECRM_ID_ALREADY_IN_USE));
      } else if (errorMessage === 'Bad Request' && e.error.detail.includes('ECRM_CONTACT_ID_USED')) {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.ECRM_CONTACT_ID_ALREADY_IN_USE));
      } else {
        this.messageService.setError(this.translateService.instant(this.MESSAGES.NOT_UPDATED));
      }
      return { updated: false };
    } finally {
      this.submittingUser$.next(false);
    }
  }

  async handleSubscriptionsSubmit(userForm: FormGroup, userId: string) {
    this.dialog.open(UserDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        initialStage: UserDialogStage.updating_subs,
      },
    });
    try {
      await this.userFormRolesService.submitSubs(userId, userForm.getRawValue());
      this.dialog.closeAll();
      this.subscriptionTableService.hasChanges$.next(false);
    } catch (error) {
      this.dialog.closeAll();
      console.error('Failed submitting subscriptions', error);
      this.messageService.setError(this.translateService.instant(this.MESSAGES.ERROR_APPLYING_SUBSCRIPTIONS));
      return;
    }
  }

  async handleSendActivationEmail(userID: string) {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        initialStage: UserDialogStage.sending_activation_email,
      },
    });
    try {
      await this.userStatusService.sendActivationEmail(userID);
      this.messageService.setSuccess(this.translateService.instant(this.MESSAGES.ACTIVATION_EMAIL_SENT));
      dialogRef.close();
    } catch (err) {
      console.log(`Could not Send Activation Email`, err);
      this.messageService.setError(this.translateService.instant(this.MESSAGES.SEND_ACTIVATION_EMAIL_ERROR));
      dialogRef.close();
      throw err;
    }
  }
}
